<div class="tab-head">
  <div>
    <h3>Portal Access</h3>
    <p>
      Logins this client's staff use to sign in to the client portal. Create the first
      admin here; they will be asked to change the password at first sign-in.
    </p>
  </div>
  <p-button label="Add Login" icon="pi pi-user-plus" (onClick)="openCreate()" />
</div>

@if (!loading() && rows().length && !hasAdmin()) {
  <div class="warn-strip">
    <i class="pi pi-exclamation-triangle"></i>
    No active <strong>client admin</strong> for this client — nobody can manage portal users on their side.
  </div>
}

<p-table
  [value]="rows()"
  [loading]="loading()"
  dataKey="id"
  styleClass="p-datatable-sm"
  [rows]="10"
  [paginator]="rows().length > 10">
  <ng-template pTemplate="header">
    <tr>
      <th>Username</th>
      <th>Name</th>
      <th>Contact</th>
      <th class="ta-c">Role</th>
      <th class="ta-c">Status</th>
      <th class="ta-r">Actions</th>
    </tr>
  </ng-template>

  <ng-template pTemplate="body" let-u>
    <tr>
      <td>
        <strong class="mono">{{ u.username }}</strong>
        @if (u.mustChangePassword) {
          <span class="sub">must change password</span>
        }
      </td>
      <td>{{ u.fullName || '—' }}</td>
      <td>
        @if (u.email) { <div class="muted-line">{{ u.email }}</div> }
        @if (u.mobile) { <div class="muted-line">{{ u.mobile }}</div> }
        @if (!u.email && !u.mobile) { <span class="muted-line">—</span> }
      </td>
      <td class="ta-c">
        @if (u.isClientAdmin) {
          <p-tag value="Client Admin" severity="info" />
        } @else {
          <p-tag value="User" severity="secondary" />
        }
      </td>
      <td class="ta-c">
        @if (u.isActive) {
          <p-tag value="Active" severity="success" />
        } @else {
          <p-tag value="Inactive" severity="danger" />
        }
      </td>
      <td class="ta-r actions">
        <p-button
          icon="pi pi-key"
          [text]="true"
          severity="secondary"
          pTooltip="Reset password"
          (onClick)="openReset(u)" />
      </td>
    </tr>
  </ng-template>

  <ng-template pTemplate="emptymessage">
    <tr>
      <td colspan="6">
        <div class="state">
          <i class="pi pi-lock"></i>
          <p>No portal logins yet. Add one so this client can sign in.</p>
        </div>
      </td>
    </tr>
  </ng-template>
</p-table>

<!-- ===================== create ===================== -->
<p-dialog
  header="Add portal login"
  [visible]="showCreate()"
  (visibleChange)="showCreate.set($event)"
  [modal]="true"
  [style]="{ width: '520px' }"
  [draggable]="true">
  <form [formGroup]="createForm" class="pu-form">
    <div class="grid-2">
      <div class="field">
        <label>Username <span class="req">*</span></label>
        <input pInputText formControlName="username" autocomplete="off" placeholder="e.g. stardigi.admin" />
        @if (invalid(createForm, 'username')) { <small class="err">At least 3 characters.</small> }
      </div>
      <div class="field">
        <label>Full name</label>
        <input pInputText formControlName="fullName" placeholder="Person's name" />
      </div>
    </div>

    <div class="grid-2">
      <div class="field">
        <label>Email</label>
        <input pInputText formControlName="email" autocomplete="off" placeholder="name@company.com" />
        @if (invalid(createForm, 'email')) { <small class="err">Enter a valid email.</small> }
      </div>
      <div class="field">
        <label>Mobile</label>
        <input pInputText formControlName="mobile" maxlength="10" placeholder="10 digits" />
        @if (invalid(createForm, 'mobile')) { <small class="err">Enter 10 digits.</small> }
      </div>
    </div>

    <div class="field">
      <label>Password <span class="req">*</span></label>
      <p-password
        formControlName="password"
        [toggleMask]="true"
        [feedback]="true"
        styleClass="w-full"
        inputStyleClass="w-full"
        placeholder="At least 8 characters" />
      @if (invalid(createForm, 'password')) { <small class="err">At least 8 characters.</small> }
      <small class="hint">
        Shown only now — it is hashed on the server and can never be read back. The user is
        asked to change it at first sign-in.
      </small>
    </div>

    <div class="switch-row">
      <p-toggleswitch formControlName="isClientAdmin" inputId="pu-admin" />
      <label for="pu-admin">Client admin — can manage this client's own users and branches</label>
    </div>
    <div class="switch-row">
      <p-toggleswitch formControlName="isActive" inputId="pu-active" />
      <label for="pu-active">Active</label>
    </div>
  </form>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" [text]="true" severity="secondary" (onClick)="showCreate.set(false)" />
    <p-button label="Create login" icon="pi pi-check" [loading]="saving()" (onClick)="create()" />
  </ng-template>
</p-dialog>

<!-- ===================== reset ===================== -->
<p-dialog
  header="Reset password"
  [visible]="showReset()"
  (visibleChange)="showReset.set($event)"
  [modal]="true"
  [style]="{ width: '460px' }"
  [draggable]="true">
  <form [formGroup]="resetForm" class="pu-form">
    <p class="reset-who">
      Setting a new password for <strong class="mono">{{ resetTarget()?.username }}</strong>.
    </p>
    <div class="field">
      <label>New password <span class="req">*</span></label>
      <p-password
        formControlName="password"
        [toggleMask]="true"
        [feedback]="true"
        styleClass="w-full"
        inputStyleClass="w-full"
        placeholder="At least 8 characters" />
      @if (invalid(resetForm, 'password')) { <small class="err">At least 8 characters.</small> }
      <small class="hint">Share it securely — it cannot be viewed again after you close this dialog.</small>
    </div>
  </form>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" [text]="true" severity="secondary" (onClick)="showReset.set(false)" />
    <p-button label="Reset password" icon="pi pi-key" [loading]="saving()" (onClick)="resetPassword()" />
  </ng-template>
</p-dialog>
